import { useEffect, useRef } from 'react';

import { TASK_NAMES } from '../../../common/utils/events/task-names';
import { onTaskSuccess } from '../../../common/utils/events/vital-stats-events';
import { getRelayNetworkTraceInfo } from '../../../common/utils/relay/get-extension-data';
import { useAnalytics } from '../../../controllers/analytics';
import { useFullPageSearchVitalStats } from '../vital-stats-context';

type useSearchResultsVitalStatsArgs = {
	edges: any[];
	searchSessionId: string;
	isCQL: boolean;
	isCachedResult?: boolean;
};

export const useSearchResultsVitalStats: ({
	edges,
	searchSessionId,
	isCQL,
	isCachedResult,
}: useSearchResultsVitalStatsArgs) => void = ({
	edges,
	searchSessionId,
	isCQL,
	isCachedResult,
}: useSearchResultsVitalStatsArgs) => {
	const { fireAnalyticsEvent } = useAnalytics();
	const vitalStats = useFullPageSearchVitalStats();
	const fullPageSearchTaskId = vitalStats.getUxTaskId();
	const networkRefs = vitalStats.getNetworkRefs();
	const asyncSearchRefs = vitalStats.getAsyncSearchRefs();
	const onNetworkSuccess = vitalStats.onNetworkSuccess;
	const onAsyncSearchSuccess = vitalStats.onAsyncSearchSuccess;
	const onAsyncSearchFail = vitalStats.onAsyncSearchFail;

	const vitalStatsEventFiredRef = useRef(false);
	const networkVitalStatsEventFiredRef = useRef(false);
	const asyncSearchVitalStatsEventFiredRef = useRef(false);
	const fullPageSearchTaskIdRefStable = useRef(fullPageSearchTaskId);
	const searchSessionIdRefStable = useRef(searchSessionId);

	// Keep refs up to date and reset vitalStatsEventFiredRef when task ID changes
	useEffect(() => {
		const taskIdChanged = fullPageSearchTaskId !== fullPageSearchTaskIdRefStable.current;

		if (taskIdChanged) {
			vitalStatsEventFiredRef.current = false;
			networkVitalStatsEventFiredRef.current = false;
			asyncSearchVitalStatsEventFiredRef.current = false;
		}

		fullPageSearchTaskIdRefStable.current = fullPageSearchTaskId;
		searchSessionIdRefStable.current = searchSessionId;
	}, [fullPageSearchTaskId, searchSessionId]);

	// Fire vital stats taskSuccess event when results are successfully rendered (UX)
	useEffect(() => {
		if (
			fullPageSearchTaskIdRefStable.current &&
			!vitalStatsEventFiredRef.current &&
			edges.length > 0
		) {
			vitalStatsEventFiredRef.current = true;
			fireAnalyticsEvent(
				onTaskSuccess({
					searchSessionId: searchSessionIdRefStable.current || '',
					taskId: fullPageSearchTaskIdRefStable.current,
					task: TASK_NAMES.SEARCH_PAGE_FULL_PAGE_SEARCH_RESULTS_UX,
				}),
			);
		}
		// Refs (fullPageSearchTaskIdRefStable, searchSessionIdRefStable, vitalStatsEventFiredRef)
		// are intentionally NOT in dependencies - we use refs to read latest values without
		// causing re-runs. fireAnalyticsEvent is stable and doesn't need to be included.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [edges.length, fullPageSearchTaskId]);

	// Fire network VitalStats taskSuccess event when results are received
	// Note: Network success fires for zero results too (edges.length === 0 is valid)
	useEffect(() => {
		if (networkRefs && onNetworkSuccess && !networkVitalStatsEventFiredRef.current) {
			const state = networkRefs.current.state;

			// Only fire if task is in 'started' state (prevents duplicates)
			if (state.state === 'started' && state.taskId) {
				networkVitalStatsEventFiredRef.current = true;
				const traceInfo = getRelayNetworkTraceInfo(isCQL);
				onNetworkSuccess(edges.length, traceInfo);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [edges.length, fullPageSearchTaskId, networkRefs, onNetworkSuccess]);

	// Fire async search VitalStats taskSuccess/taskFail when results are received
	// Only fires when an async search task has been started (cacheExpected=true)
	// Success = aggregator served from backend cache (isCachedResult=true)
	// Fail = aggregator did not serve from cache (isCachedResult=false/undefined)
	useEffect(() => {
		if (
			!asyncSearchRefs ||
			!onAsyncSearchSuccess ||
			!onAsyncSearchFail ||
			asyncSearchVitalStatsEventFiredRef.current
		) {
			return;
		}

		const state = asyncSearchRefs.current.state;

		// Only fire if async search task is in 'started' state (prevents duplicates)
		if (state.state === 'started' && state.taskId) {
			asyncSearchVitalStatsEventFiredRef.current = true;
			if (isCachedResult) {
				onAsyncSearchSuccess();
			} else {
				onAsyncSearchFail();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		edges.length,
		fullPageSearchTaskId,
		asyncSearchRefs,
		onAsyncSearchSuccess,
		onAsyncSearchFail,
		isCachedResult,
	]);
};
